import { collection, doc, getDoc, getDocs, limit, orderBy, query, where, startAt, endAt, addDoc, serverTimestamp, setDoc, startAfter, updateDoc, type QueryDocumentSnapshot } from 'firebase/firestore';
import { db, call } from '../lib/firebase';
import { normaliseName, searchKeys, type MemberRank } from '../domain/index.js';

export interface Member {
  id: string; uid?: string | null; firstName: string; lastName: string; preferredName?: string; email?: string; phone?: string;
  status: string; rank?: MemberRank & { systemId: string }; tags?: string[]; lastAttendedAt?: number; totalClasses?: number;
  billing?: { status: string; pastDueSince?: number; failedAttempts?: number }; billingAccountId?: string;
  emergencyContact?: { name: string; phone: string }; marketingConsent?: boolean; privacyConsentAt?: number; joinedAt?: number; searchName: string; source?: string;
}

export const displayName = (m: Pick<Member, 'firstName' | 'lastName' | 'preferredName'>) => `${m.preferredName || m.firstName} ${m.lastName}`.trim();

/** Prefix search: name prefix OR any word prefix. Max ~2 small indexed queries per keystroke. */
export async function searchMembers(q: string, n = 10): Promise<Member[]> {
  const s = normaliseName(q);
  if (s.length < 2) return [];
  const [byName, byKey] = await Promise.all([
    getDocs(query(collection(db, 'members'), orderBy('searchName'), startAt(s), endAt(s + '\uf8ff'), limit(n))),
    s.includes(' ') ? null : getDocs(query(collection(db, 'members'), where('searchKeys', 'array-contains', s.slice(0, 20)), limit(n))),
  ]);
  const all = new Map<string, Member>();
  [...byName.docs, ...(byKey?.docs ?? [])].forEach(d => all.set(d.id, { id: d.id, ...d.data() } as Member));
  return [...all.values()].filter(m => m.status !== 'anonymised').slice(0, n);
}

export async function listMembers(status: string, after?: QueryDocumentSnapshot) {
  const cons = [...(status === 'all' ? [] : [where('status', '==', status)]), orderBy('searchName'), ...(after ? [startAfter(after)] : []), limit(25)];
  const snap = await getDocs(query(collection(db, 'members'), ...cons));
  return { members: snap.docs.map(d => ({ id: d.id, ...d.data() }) as Member), last: snap.docs.at(-1) };
}

export const getMember = async (id: string) => { const d = await getDoc(doc(db, 'members', id)); return d.exists() ? ({ id: d.id, ...d.data() } as Member) : null; };

/** Update only a member's name fields. Firestore rules restrict these edits to owner/admin roles. */
export async function updateMemberName(id: string, input: { firstName: string; lastName: string; preferredName?: string }) {
  const firstName = input.firstName.trim();
  const lastName = input.lastName.trim();
  const preferredName = input.preferredName?.trim() ?? '';
  if (!firstName || !lastName) throw new Error('First and last name are required. Nothing was saved.');
  if (firstName.length > 60 || lastName.length > 80 || preferredName.length > 60) throw new Error('Names are too long. First and preferred names must be 60 characters or fewer; last name must be 80 or fewer.');
  const patch = { firstName, lastName, preferredName: preferredName || null,
    searchName: normaliseName(`${firstName} ${lastName}`), searchKeys: searchKeys(firstName, lastName), updatedAt: serverTimestamp() };
  await updateDoc(doc(db, 'members', id), patch);
  return { firstName, lastName, preferredName: preferredName || undefined, searchName: patch.searchName, searchKeys: patch.searchKeys };
}

/** Staff-created member (walk-in). uid stays null until they sign up with the same email. */
export async function createMember(input: { firstName: string; lastName: string; email?: string; phone?: string; privacyConsent: boolean; marketingConsent: boolean }) {
  const ref = doc(collection(db, 'members'));
  const { privacyConsent, ...rest } = input;
  await setDoc(ref, { ...rest, email: input.email?.toLowerCase() ?? null, phone: input.phone ?? null, uid: null, privacyConsentAt: privacyConsent ? Date.now() : null,
    status: 'lead', searchName: normaliseName(`${input.firstName} ${input.lastName}`), searchKeys: searchKeys(input.firstName, input.lastName),
    billingAccountId: ref.id, tags: [], totalClasses: 0, joinedAt: Date.now(), source: 'staff', createdAt: serverTimestamp() });
  return ref.id;
}

export const addNote = (memberId: string, text: string, authorUid: string) => addDoc(collection(db, 'members', memberId, 'notes'), { text, authorUid, at: Date.now() });

export const registerSelf = call<{ firstName: string; lastName: string; phone?: string; privacyConsent: boolean; marketingConsent: boolean }, { memberId: string; existing: boolean }>('registerSelf');
export const setCheckInPin = call<{ memberId: string; pin: string }, { ok: boolean }>('setCheckInPin');
export const exportMyData = call<{ memberId?: string }, unknown>('exportMyData');
export const inviteStaff = call<{ email: string; role: string }, { ok: boolean; note: string }>('inviteStaff');
